import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  ArrowLeft, 
  FileImage, 
  AlertTriangle, 
  Stethoscope, 
  ListChecks, 
  FlaskConical,
  Clock,
  Loader2,
  Copy,
  Check,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Separator } from "../../components/ui/separator";
import { Skeleton } from "../../components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";
import { toast } from "sonner";
import axios from "axios";
import { LabsLayout } from "../../components/LabsLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/labs`;

export default function AnalysisPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    findings: true,
    impression: true,
    differentials: true,
    recommendations: true,
    redFlags: true,
    interpretation: false
  });

  useEffect(() => {
    fetchAnalysis();
  }, [id]);

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/interpretations/${id}`, { withCredentials: true });
      setAnalysis(response.data);
    } catch (error) {
      console.error("Error fetching analysis:", error);
      toast.error("Failed to load analysis");
      navigate("/labs");
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const copyToClipboard = async () => {
    if (!analysis) return;
    
    const text = `
Medical Image Analysis Report
=============================
Scan Type: ${analysis.scan_type}
Date: ${new Date(analysis.created_at).toLocaleDateString()}

FINDINGS:
${analysis.findings.map((f, i) => `${i + 1}. ${f}`).join('\n')}

IMPRESSION:
${analysis.impression}

DIFFERENTIAL DIAGNOSES:
${analysis.differential_diagnoses.map((d, i) => `${i + 1}. ${d}`).join('\n')}

RECOMMENDED TESTS:
${analysis.recommended_tests.map((r, i) => `${i + 1}. ${r}`).join('\n')}

RED FLAGS:
${analysis.red_flags.map((r, i) => `${i + 1}. ${r}`).join('\n')}

FULL INTERPRETATION:
${analysis.full_interpretation}
    `.trim();

    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Report copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <LabsLayout>
        <LoadingSkeleton />
      </LabsLayout>
    );
  }

  if (!analysis) {
    return (
      <LabsLayout>
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-[#09090B]">
          <div className="text-center">
            <p className="text-zinc-400 mb-4">Analysis not found</p>
            <Button onClick={() => navigate("/labs")} data-testid="go-home-btn">
              Go Back Home
            </Button>
          </div>
        </div>
      </LabsLayout>
    );
  }

  return (
    <LabsLayout>
      <div className="min-h-[calc(100vh-4rem)] py-8 bg-[#09090B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/labs")}
                data-testid="back-btn"
                className="text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-manrope text-white">
                  Analysis Results
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <Badge variant="secondary" className="bg-zinc-800 text-zinc-300">
                    {analysis.scan_type || "Medical Image"}
                  </Badge>
                  <span className="text-sm text-zinc-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(analysis.created_at).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            <Button
              onClick={copyToClipboard}
              variant="outline"
              data-testid="copy-report-btn"
              className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 text-zinc-300"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Report
                </>
              )}
            </Button>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column - Main Analysis */}
            <div className="lg:col-span-8 space-y-6">
              {/* Impression Card - Most Important */}
              <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
                <div className="bg-blue-600/10 border-b border-blue-600/20 p-4">
                  <div className="flex items-center gap-3">
                    <Stethoscope className="w-6 h-6 text-blue-500" />
                    <h2 className="text-xl font-semibold font-manrope text-white">
                      Radiological Impression
                    </h2>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-lg text-zinc-200 leading-relaxed" data-testid="impression-text">
                    {analysis.impression || "No impression available"}
                  </p>
                </CardContent>
              </Card>

              {/* Findings */}
              <CollapsibleSection
                icon={ListChecks}
                title="Key Findings"
                isOpen={expandedSections.findings}
                onToggle={() => toggleSection("findings")}
                testId="findings-section"
              >
                <div className="space-y-3">
                  {analysis.findings && analysis.findings.length > 0 ? (
                    analysis.findings.map((finding, index) => (
                      <div 
                        key={index} 
                        className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/50"
                        data-testid={`finding-${index}`}
                      >
                        <span className="w-6 h-6 rounded-full bg-blue-600/20 text-blue-400 flex items-center justify-center text-sm font-medium flex-shrink-0">
                          {index + 1}
                        </span>
                        <span className="text-zinc-300">{finding}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-zinc-500">No specific findings identified</p>
                  )}
                </div>
              </CollapsibleSection>

              {/* Full Interpretation */}
              <CollapsibleSection
                icon={FileImage}
                title="Full Interpretation"
                isOpen={expandedSections.interpretation}
                onToggle={() => toggleSection("interpretation")}
                testId="interpretation-section"
              >
                <div className="whitespace-pre-wrap text-zinc-300" data-testid="full-interpretation">
                  {analysis.full_interpretation || "No detailed interpretation available"}
                </div>
              </CollapsibleSection>
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              {/* Red Flags */}
              {analysis.red_flags && analysis.red_flags.length > 0 && (
                <Card className="bg-zinc-900/50 border-red-900/50 overflow-hidden">
                  <CardHeader className="bg-red-950/30 border-b border-red-900/30 pb-4">
                    <CardTitle className="flex items-center gap-3 text-red-400">
                      <AlertTriangle className="w-5 h-5" />
                      Red Flag Symptoms
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-2" data-testid="red-flags-list">
                    {analysis.red_flags.map((flag, index) => (
                      <div key={index} className="p-2 rounded bg-red-950/20 border border-red-900/30" data-testid={`red-flag-${index}`}>
                        <p className="text-sm text-red-200">{flag}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Differential Diagnoses */}
              {analysis.differential_diagnoses && analysis.differential_diagnoses.length > 0 && (
                <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
                  <CardHeader className="bg-amber-950/20 border-b border-amber-900/20 pb-4">
                    <CardTitle className="flex items-center gap-3 text-amber-400 text-base">
                      <FlaskConical className="w-5 h-5" />
                      Differential Diagnoses
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-2" data-testid="differentials-list">
                    {analysis.differential_diagnoses.map((diagnosis, index) => (
                      <div key={index} className="p-2 rounded bg-amber-950/20 border border-amber-900/20" data-testid={`differential-${index}`}>
                        <p className="text-sm text-amber-200">{diagnosis}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Recommended Tests */}
              {analysis.recommended_tests && analysis.recommended_tests.length > 0 && (
                <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
                  <CardHeader className="bg-emerald-950/20 border-b border-emerald-900/20 pb-4">
                    <CardTitle className="flex items-center gap-3 text-emerald-400 text-base">
                      <Stethoscope className="w-5 h-5" />
                      Recommended Tests
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-2" data-testid="recommendations-list">
                    {analysis.recommended_tests.map((test, index) => (
                      <div key={index} className="p-2 rounded bg-emerald-950/20 border border-emerald-900/20" data-testid={`recommendation-${index}`}>
                        <p className="text-sm text-emerald-200">{test}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4 space-y-3">
                  <Button
                    onClick={() => navigate("/labs")}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="new-analysis-btn"
                  >
                    New Analysis
                  </Button>
                  <Button
                    onClick={() => navigate("/labs/history")}
                    variant="outline"
                    className="w-full bg-zinc-900 border-zinc-700 hover:bg-zinc-800"
                    data-testid="view-history-btn"
                  >
                    View History
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </LabsLayout>
  );
}

const CollapsibleSection = ({ icon: Icon, title, isOpen, onToggle, children, testId }) => (
  <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <button
          className="w-full flex items-center justify-between p-4 hover:bg-zinc-800/50 transition-colors"
          data-testid={`${testId}-toggle`}
        >
          <div className="flex items-center gap-3">
            <Icon className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-semibold font-manrope text-white">{title}</h3>
          </div>
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-zinc-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-zinc-400" />
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <Separator className="bg-zinc-800" />
        <div className="p-4" data-testid={testId}>
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  </Card>
);

const LoadingSkeleton = () => (
  <div className="min-h-[calc(100vh-4rem)] py-8 bg-[#09090B]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex items-center gap-4 mb-8">
        <Skeleton className="w-10 h-10 rounded-lg bg-zinc-800" />
        <div className="space-y-2">
          <Skeleton className="w-48 h-8 bg-zinc-800" />
          <Skeleton className="w-32 h-4 bg-zinc-800" />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <Skeleton className="w-full h-40 rounded-lg bg-zinc-800" />
          <Skeleton className="w-full h-64 rounded-lg bg-zinc-800" />
        </div>
        <div className="lg:col-span-4 space-y-6">
          <Skeleton className="w-full h-48 rounded-lg bg-zinc-800" />
          <Skeleton className="w-full h-48 rounded-lg bg-zinc-800" />
        </div>
      </div>
    </div>
  </div>
);
